import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import { PageSeo } from '../components/Seo';
import { useProducts } from '../context/ProductContext';
import { getProductCategories, getProductsByCategory } from '../utils/product';
import { breadcrumbJsonLd, itemListJsonLd } from '../utils/structuredData';

export default function Shop() {
  const { products, isLoading, refreshProducts } = useProducts();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');

  useEffect(() => {
    void refreshProducts({ silent: products.length > 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshProducts]);

  const activeCategory = searchParams.get('category') ?? 'Tümü';
  const categories = useMemo(() => getProductCategories(products), [products]);

  const filteredProducts = useMemo(() => {
    let result = getProductsByCategory(products, activeCategory);
    if (search.trim()) {
      const query = search.toLowerCase();
      result = result.filter(
        (p) =>
          p.name.toLowerCase().includes(query) ||
          p.description.toLowerCase().includes(query) ||
          p.category.toLowerCase().includes(query),
      );
    }
    return result;
  }, [products, activeCategory, search]);

  const setCategory = (category: string) => {
    if (category === 'Tümü') {
      searchParams.delete('category');
    } else {
      searchParams.set('category', category);
    }
    setSearchParams(searchParams);
  };

  const seoTitle =
    activeCategory !== 'Tümü' ? `${activeCategory} Ürünleri` : 'Ürünler';
  const seoDescription =
    activeCategory !== 'Tümü'
      ? `${activeCategory} kategorisindeki sağlık ve longevity cihazlarını inceleyin.`
      : 'tDCS, CES, hidrojenli su ve LED ışık terapisi cihazları. Premium sağlık ürünlerini keşfedin.';

  if (isLoading) {
    return (
      <div className="container empty-state">
        <p>Ürünler yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="container shop-page">
      <PageSeo
        title={seoTitle}
        description={seoDescription}
        path={activeCategory !== 'Tümü' ? `/shop?category=${encodeURIComponent(activeCategory)}` : '/shop'}
        jsonLd={[
          breadcrumbJsonLd([
            { name: 'Ana Sayfa', path: '/' },
            { name: 'Ürünler', path: '/shop' },
            ...(activeCategory !== 'Tümü'
              ? [{ name: activeCategory, path: `/shop?category=${encodeURIComponent(activeCategory)}` }]
              : []),
          ]),
          itemListJsonLd(filteredProducts),
        ]}
      />
      <div className="page-header">
        <h1>Ürünler</h1>
        <p>Premium sağlık cihazları — tDCS, CES, hidrojen ve ışık terapisi</p>
      </div>

      <div className="shop-toolbar">
        <div className="category-filters">
          {categories.map((cat) => (
            <button
              key={cat}
              className={`filter-btn ${activeCategory === cat ? 'active' : ''}`}
              onClick={() => setCategory(cat)}
            >
              {cat}
            </button>
          ))}
        </div>

        <input
          type="search"
          placeholder="Ürün ara..."
          className="search-input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {filteredProducts.length === 0 ? (
        <div className="empty-state">
          <p>Aradığınız kriterlere uygun ürün bulunamadı.</p>
        </div>
      ) : (
        <div className="product-grid">
          {filteredProducts.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      )}
    </div>
  );
}
